import { useQuery } from "@tanstack/react-query";
import { qk } from "@/api/queryKeys";
import { publicService } from "@/services/publicService";

export type SiteContent = {
  home: {
    heroEyebrow: string;
    heroTitle: string;
    heroAccent: string;
    heroDescription: string;
    heroImageUrl: string | null;
    primaryCtaLabel: string;
    secondaryCtaLabel: string;
  };
  about: {
    eyebrow: string;
    title: string;
    subtitle: string;
    heroImageUrl: string | null;
    heading: string;
    bodyOne: string;
    bodyTwo: string;
    featureImageUrl: string | null;
    secondaryImageUrl: string | null;
    tertiaryImageUrl: string | null;
  };
  history: {
    eyebrow: string;
    title: string;
    subtitle: string;
    heroImageUrl: string | null;
    introduction: string;
  };
  mission: {
    eyebrow: string;
    title: string;
    heroImageUrl: string | null;
    missionText: string;
    visionText: string;
    valuesText: string;
  };
  teams: {
    eyebrow: string;
    title: string;
    subtitle: string;
    heroImageUrl: string | null;
  };
  footer: { description: string };
};

export const defaultSiteContent: SiteContent = {
  home: {
    heroEyebrow: "The official home",
    heroTitle: "Rise. Play.",
    heroAccent: "Belong.",
    heroDescription:
      "The official home of Soweto Super United FC. Live match centre, fixtures, results, exclusive stories and full-match broadcasts — all in one place.",
    heroImageUrl: "/assets/club/team-hero.webp",
    primaryCtaLabel: "Watch Live",
    secondaryCtaLabel: "See fixtures",
  },
  about: {
    eyebrow: "Our club",
    title: "Built by Soweto. For Soweto.",
    subtitle:
      "Founded in 2017, Soweto Super United Football Club is a football club based in Soweto.",
    heroImageUrl: "/assets/club/team-and-staff.webp",
    heading: "Who we are",
    bodyOne:
      "Founded in 2017, Soweto Super United Football Club (SSUFC) is a football club based in Soweto.",
    bodyTwo:
      "Simply known as Top Dogz — Izinj Ezistrong, we pride ourselves on nurturing kasi football talent.",
    featureImageUrl: "/assets/club/team-hero.webp",
    secondaryImageUrl: null,
    tertiaryImageUrl: null,
  },
  history: {
    eyebrow: "Since 2017",
    title: "Our history",
    subtitle: "Key moments in the making of Soweto Super United FC.",
    heroImageUrl: "/assets/club/team-and-staff.webp",
    introduction:
      "Founded in Soweto in 2017, SSUFC has built its identity around nurturing kasi football talent and competing with the spirit of the Top Dogz — Izinj Ezistrong.",
  },
  mission: {
    eyebrow: "What we stand for",
    title: "Mission. Vision. Values.",
    heroImageUrl: "/assets/club/team-and-staff.webp",
    missionText:
      "To develop footballers and citizens who represent Soweto with pride, discipline and joy on and off the pitch.",
    visionText:
      "A club recognised across South Africa for its identity — technical, brave, community-rooted, and impossible to beat at home.",
    valuesText:
      "Pride. Discipline. Freedom. Family. Every decision, from selection to sponsorship, is measured against these four words.",
  },
  teams: {
    eyebrow: "Squads",
    title: "Our teams",
    subtitle: "From First Team to Academy — one club, one badge.",
    heroImageUrl: "/assets/club/team-hero.webp",
  },
  footer: {
    description:
      "Founded in 2017 in Soweto. Top Dogz — Izinj Ezistrong. Proudly nurturing kasi football talent.",
  },
};

export function normaliseSiteContent(value: unknown): SiteContent {
  if (!value || typeof value !== "object") return defaultSiteContent;
  const stored = value as Partial<Record<keyof SiteContent, Record<string, unknown>>>;
  return Object.fromEntries(
    Object.entries(defaultSiteContent).map(([section, defaults]) => [
      section,
      { ...defaults, ...(stored[section as keyof SiteContent] ?? {}) },
    ]),
  ) as SiteContent;
}

export function useSiteContent() {
  const query = useQuery({ queryKey: qk.public.settings, queryFn: publicService.settings });
  return { ...query, content: normaliseSiteContent(query.data?.siteContent) };
}
